import React, { useState, useEffect } from 'react';
import { useApp } from '../contexts/AppContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Sheet, 
  SheetContent, 
  SheetDescription, 
  SheetHeader, 
  SheetTitle,
  SheetTrigger 
} from './ui/sheet';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Plus, MoreHorizontal } from 'lucide-react';
import { Transaction } from '../lib/types';

interface QuickAddProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  trigger?: React.ReactNode;
}

export function QuickAdd({ isOpen, onOpenChange, trigger }: QuickAddProps) {
  const { getAccounts, getCategories, addTransaction, state } = useApp();
  
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [payee, setPayee] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountId, setAccountId] = useState('');
  const [memo, setMemo] = useState('');
  const [tags, setTags] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const accounts = getAccounts();
  const categories = getCategories();

  // Set defaults
  useEffect(() => {
    if (accounts.length > 0 && !accountId) {
      // Default to last used non-closed account or first active account
      const activeAccounts = accounts.filter(acc => acc.status === 'open');
      if (activeAccounts.length > 0) {
        setAccountId(activeAccounts[0].id);
      }
    }
  }, [accounts, accountId]);

  // Load last used category from localStorage
  useEffect(() => {
    const lastCategoryId = localStorage.getItem('quickAdd.lastCategoryId');
    if (lastCategoryId && categories.some(c => c.id === lastCategoryId)) {
      setCategoryId(lastCategoryId);
    }
  }, [categories]);

  const resetForm = () => {
    setAmount('');
    setPayee('');
    setMemo('');
    setTags('');
    setShowAdvanced(false);
    setDate(new Date().toISOString().split('T')[0]);
    // Keep categoryId and accountId for convenience
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!amount.trim() || !categoryId || !accountId) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Evaluate mathematical expressions in amount
      const evaluatedAmount = evaluateExpression(amount);
      if (isNaN(evaluatedAmount)) {
        throw new Error('Invalid amount');
      }

      // Save last used category
      localStorage.setItem('quickAdd.lastCategoryId', categoryId);
      localStorage.setItem('quickAdd.lastAccountId', accountId);

      const transaction: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'> = {
        date,
        accountId,
        amount: evaluatedAmount,
        payee: payee.trim() || (evaluatedAmount > 0 ? 'Income' : 'Expense'),
        categoryId,
        memo: memo.trim() || undefined,
        tags: tags.trim() ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        cleared: false,
        reconciled: false
      };

      addTransaction(transaction);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to add transaction:', error);
      // Could add error state here
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as any);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      
      <SheetContent side="bottom" className="h-auto">
        <SheetHeader>
          <SheetTitle>Quick Add Transaction</SheetTitle>
          <SheetDescription>
            Add a transaction quickly with just the essentials
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4" onKeyDown={handleKeyDown}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Amount - Required */}
            <div>
              <Label htmlFor="amount" className="text-sm font-medium">
                Amount *
              </Label>
              <Input
                id="amount"
                type="text"
                placeholder="-50 or 100+25 or 1500"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1"
                autoFocus
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Negative = expense, positive = income. Math supported (+ - * /).
              </p>
            </div>

            {/* Category - Required */}
            <div>
              <Label htmlFor="category" className="text-sm font-medium">
                Category *
              </Label>
              <Select value={categoryId} onValueChange={setCategoryId} required>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(
                    categories.reduce((groups, category) => {
                      const group = category.group || 'Other';
                      if (!groups[group]) groups[group] = [];
                      groups[group].push(category);
                      return groups;
                    }, {} as Record<string, typeof categories>)
                  ).map(([group, groupCategories]) => (
                    <div key={group}>
                      <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                        {group}
                      </div>
                      {groupCategories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Payee - Optional */}
          <div>
            <Label htmlFor="payee" className="text-sm font-medium">
              Payee (optional)
            </Label>
            <Input
              id="payee"
              type="text"
              placeholder="Store name, person, etc."
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              className="mt-1"
            />
          </div>

          {/* Advanced Options */}
          {showAdvanced && (
            <div className="space-y-4 border-t pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date" className="text-sm font-medium">
                    Date
                  </Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="account" className="text-sm font-medium">
                    Account
                  </Label>
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts
                        .filter(acc => acc.status === 'open')
                        .map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name} ({account.type})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="memo" className="text-sm font-medium">
                  Memo
                </Label>
                <Input
                  id="memo"
                  type="text"
                  placeholder="Additional notes"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="tags" className="text-sm font-medium">
                  Tags
                </Label>
                <Input
                  id="tags"
                  type="text"
                  placeholder="tag1, tag2, tag3"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm"
            >
              <MoreHorizontal className="w-4 h-4 mr-2" />
              {showAdvanced ? 'Hide' : 'More'} Details
            </Button>

            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !amount.trim() || !categoryId || !accountId}
              >
                {isSubmitting ? 'Adding...' : 'Add Transaction'}
              </Button>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Press Ctrl/Cmd + Enter to save quickly
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}

// Quick Add FAB Component
export function QuickAddFAB() {
  const [isOpen, setIsOpen] = useState(false);

  // Global hotkey support (N key)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'n' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        // Check if we're not in an input field
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          e.preventDefault();
          setIsOpen(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <QuickAdd
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        <Button
          size="lg"
          className="fixed bottom-6 right-6 md:bottom-6 md:left-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-shadow"
          aria-label="Quick Add Transaction (Press N)"
        >
          <Plus className="h-6 w-6" />
        </Button>
      }
    />
  );
}

// Simple expression evaluator for math in amount field
function evaluateExpression(expr: string): number {
  try {
    // Remove whitespace and validate characters
    const cleaned = expr.replace(/\s/g, '');
    
    // Only allow numbers, decimal points, and basic operators
    if (!/^[0-9+\-*/.()]+$/.test(cleaned)) {
      return parseFloat(cleaned); // Fallback to simple parsing
    }
    
    // Evaluate safely (basic expressions only)
    // This is a simple implementation - could be enhanced with a proper parser
    const result = Function(`"use strict"; return (${cleaned})`)();
    return typeof result === 'number' && !isNaN(result) ? result : parseFloat(expr);
  } catch {
    return parseFloat(expr);
  }
}